import React, { ChangeEvent } from "react";

import { V2 } from "../../Api";
import withTitle from "../withTitle";

import "../bigsheet.css";
import { DateTime } from "luxon";
import { formatDateLocalNumeric, formatDateLocalWeekdayOnly, formatDateLocalMonth, formatName } from "../../Utilities";
import DelayInput from "../DelayInput";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";
import { Link } from "react-router-dom";
import TabControl from "../TabControl";
import { WellKnownGroups } from "../AuthContext";

interface IMetricsBigSheetViewState {
    isLoading: boolean | string;
    isRefreshEnabled: boolean;

    providers: any[];
    providerGroups: {
        [date: string]: { groups: any[] }
    };
    providerGroupsMonth: { groups: any[] };
    ytdProviders: any[];
    ytdProviderGroupsMonth: { groups: any[] };

    schedule: {
        [date: string]: {
            [id: string]: any
        }
    };

    goals: {
        [name: string]: {
            [cpt: string]: {
                dayTarget: number,
                weekTarget: number,
                monthTarget: number,
                employees: any,
                locations: any,
                groups: any
            }
        }
    };

    data: { [date: string]: any };
    ytdData: { [date: string]: any };

    month: string;

    colors: any[];
    statusFilter: string;
    showGoals: boolean;

    [name: string]: any;
}

const TOTALS_CPT = "T0000";
const REFRESH_INTERVAL = 5 * 60000;

class MetricsBigSheetView extends React.Component<{}, IMetricsBigSheetViewState> {
    private cpt_list = ["58558", "58563", "57288", "58570", "58660", TOTALS_CPT];
    private _refreshTimer?: number;

    constructor(props: Readonly<{}>) {
        super(props);

        const state: IMetricsBigSheetViewState = {
            isLoading: true,
            isRefreshEnabled: true,

            month: DateTime.local().toFormat("yyyy-MM"),

            providers: [],
            providerGroups: {},
            providerGroupsMonth: { groups: [] },
            data: {},

            ytdProviders: [],
            ytdProviderGroupsMonth: { groups: [] },
            ytdData: {},

            schedule: {},

            goals: {},

            colors: [],
            statusFilter: "",
            showGoals: true,
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
        this.toggleRefresh = this.toggleRefresh.bind(this);

        this._refreshTimer = window.setInterval(() => this.loadData(true), REFRESH_INTERVAL);
    }

    public componentDidUpdate(prevProps: Readonly<{}>, prevState: Readonly<IMetricsBigSheetViewState>) {
        if (prevState.isRefreshEnabled !== this.state.isRefreshEnabled) {
            if (this.state.isRefreshEnabled) {
                // Enable refresh
                this._refreshTimer = window.setInterval(() => this.loadData(true), REFRESH_INTERVAL);
            }
            else if (this._refreshTimer) {
                // Disable refresh
                window.clearInterval(this._refreshTimer);
                this._refreshTimer = undefined;
            }
        }
    }

    public async componentDidMount() {
        this.loadData();
    }

    public componentWillUnmount() {
        if (this._refreshTimer) {
            window.clearInterval(this._refreshTimer);
            this._refreshTimer = undefined;
        }
    }

    public render() {
        let monthStart = DateTime.fromISO(this.state.month);
        if (monthStart.weekday > 5) {
            // Started on weekend
            monthStart = monthStart.plus({ day: 2 })
        }
        monthStart = monthStart.startOf("week");

        const monthEnd = DateTime.fromISO(this.state.month).endOf("month").endOf("week").minus({ day: 2 }).startOf("day");
        const weekCount = Math.ceil(monthEnd.diff(monthStart, "days").days / 7);

        const weeks = [];
        const monthTotalData: { [cpt: string]: any, daysWorked: number } = { daysWorked: 0 };
        this.cpt_list.forEach(a => monthTotalData[a] = {});

        for (let i = 0; i < weekCount; i++) {
            weeks.push(this.renderWeek(i + 1, monthStart.plus({ days: 7 * i }), monthTotalData));
        }

        const monthEndGoals = this.state.goals[Object.keys(this.state.goals)[Object.keys(this.state.goals).length - 1]];
        const totals = this.renderTotals(-1, monthTotalData, (this.state.providerGroupsMonth || { groups: [] }).groups, monthEndGoals, DateTime.fromISO(this.state.month), monthTotalData.daysWorked);
        const ytdTotals = this.renderTotals(-2, this.state.ytdData, (this.state.ytdProviderGroupsMonth || { groups: [] }).groups, {}, DateTime.fromISO(this.state.month), 0);

        return (
            <div className="viewer-root viewer-full full-height full-height-max">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="metrics:cs:write">
                            <Link to="/metrics/case-stats/goals/edit"><i className="icon-pencil" /><span>Edit goals</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem>
                            <button type="button" onClick={this.toggleRefresh}><i className={this.state.isRefreshEnabled ? "icon-times-circle-o" : "icon-repeat"} /><span>{this.state.isRefreshEnabled ? "Disable" : "Enable"} auto-refresh</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <div className="filter-control-box">
                    <DelayInput type="month" className="fill-space" timeout={500} value={this.state.month} name="month" onChangeDelayed={this.handleElementChange} />
                </div>

                <TabControl headers={["All Cases", "Completed Cases", "Canceled Cases", "Outstanding"]} onTabChanged={this.handleTabChange}>
                    <div /><div /><div /><div />
                </TabControl>
                {
                    this.state.isLoading === true
                        ? <div className="spinner spinner-center"><div className="spinner-center lds-dual-ring-big" /><br /><em>Crunching the numbers...</em></div>
                        : typeof this.state.isLoading === "string"
                            ? <div className="error-box">{this.state.isLoading}</div>
                            : <div className="table-viewer bigsheet-viewer">
                                <table className="table-bigsheet">
                                    <tbody>
                                        {weeks}
                                        {totals}
                                    </tbody>
                                </table>
                                <table className="table-bigsheet">
                                    <tbody>
                                        {ytdTotals}
                                    </tbody>
                                </table>
                            </div>
                }
            </div>
        );
    }

    private renderWeek(week: number, startDate: DateTime, monthTotal: any) {
        const providerGroups = (this.state.providerGroups[startDate.minus({ day: 1 }).toISODate()] || {}).groups || [];

        const heading: React.ReactChild[] = [
            <tr key={`${week}_head`}>
                <th colSpan={2} className="bs-week gray bs-border-all">Week {week}</th>
                {this.cpt_list.map(a => this.renderOperation(0, a, {}, providerGroups, {}, this.dayGoalSelector))}
            </tr>
        ];

        const days: React.ReactChild[] = [];
        const weekTotals: { [cpt: string]: any } = {};
        this.cpt_list.forEach(a => weekTotals[a] = {});

        let daysWorked = 0;
        for (let i = 0; i < 5; i++) {
            const date = startDate.plus({ day: i });
            if (this.state.schedule[date.toISODate()]) {
                daysWorked++;
            }
            Array.prototype.push.call(days, this.renderDay(date, providerGroups, weekTotals));
        }

        monthTotal.daysWorked += daysWorked;

        this.cpt_list.forEach(cpt => {
            Object.keys(weekTotals[cpt]).forEach(np => {
                if (!monthTotal[cpt][np]) {
                    monthTotal[cpt][np] = {
                        count: 0, locations: {}
                    };
                }

                monthTotal[cpt][np].count += weekTotals[cpt][np].count || 0;
                for (const key of Object.keys(weekTotals[cpt][np].locations)) {
                    if (!monthTotal[cpt][np].locations[key]) {
                        monthTotal[cpt][np].locations[key] = { count: 0, color: weekTotals[cpt][np].locations[key].color };
                    }

                    monthTotal[cpt][np].locations[key].count += weekTotals[cpt][np].locations[key].count;
                }
            });
        });

        // Week totals
        const weekGoals = this.state.goals[startDate.plus({ day: 4 }).toISODate()];
        const totals = this.renderTotals(week, weekTotals, providerGroups, weekGoals, undefined, daysWorked);

        return heading.concat(days).concat(totals);
    }

    private renderDay(date: DateTime, providerGroups: any[], weekTotals: any, dataOverride?: any): React.ReactChild[] {
        const dayData = dataOverride || this.state.data[date.toISODate()];
        const daySchedule = this.state.schedule[date.toISODate()];
        const goals = this.state.goals[date.toISODate()] || {};
        if (!dayData) {
            return [
                <tr key={date.toISODate() + "1"} className="bs-names">
                    <td rowSpan={4} className="bs-week-date gray bs-border-left col-bold">{formatDateLocalNumeric(date)}</td>
                    <td rowSpan={4} className="bs-week-date gray bs-border-right col-bold">{formatDateLocalWeekdayOnly(date)}</td>
                    {this.cpt_list.map(a => this.renderOperation(1, a, daySchedule, providerGroups, {}, this.dayGoalSelector))}
                </tr>,
                <tr key={date.toISODate() + "2"}>
                    {this.cpt_list.map(a => this.renderOperation(2, a, daySchedule, providerGroups, {}, this.dayGoalSelector))}
                </tr>,
                <tr key={date.toISODate() + "3"}>
                    {this.cpt_list.map(a => this.renderOperation(3, a, daySchedule, providerGroups, {}, this.dayGoalSelector))}
                </tr>,
                <tr key={date.toISODate() + "4"}>
                    {this.cpt_list.map(a => this.renderOperation(4, a, daySchedule, providerGroups, {}, this.dayGoalSelector))}
                </tr>,
                <tr key={date.toISODate() + "5"}>
                    <td colSpan={2} className="black bs-border-all" />
                    {this.cpt_list.map(a => this.renderOperation(5, a, daySchedule, providerGroups, {}, this.dayGoalSelector))}
                </tr>,
            ];
        }

        return [
            <tr key={date.toISODate() + "1"} className="bs-names">
                <td rowSpan={4} className="bs-week-date gray bs-border-left col-bold">{formatDateLocalNumeric(date)}</td>
                <td rowSpan={4} className="bs-week-date gray bs-border-right col-bold">{formatDateLocalWeekdayOnly(date)}</td>
                {this.cpt_list.map(a => this.renderOperation(1, a, daySchedule, providerGroups, {}, this.dayGoalSelector, dayData[a], weekTotals[a]))}
            </tr>,
            <tr key={date.toISODate() + "2"}>
                {this.cpt_list.map(a => this.renderOperation(2, a, daySchedule, providerGroups, goals[a], this.dayGoalSelector, dayData[a], weekTotals[a]))}
            </tr>,
            <tr key={date.toISODate() + "3"}>
                {this.cpt_list.map(a => this.renderOperation(3, a, daySchedule, providerGroups, goals[a], this.dayGoalSelector, dayData[a], weekTotals[a]))}
            </tr>,
            <tr key={date.toISODate() + "4"}>
                {this.cpt_list.map(a => this.renderOperation(4, a, daySchedule, providerGroups, goals[a], this.dayGoalSelector, dayData[a], weekTotals[a]))}
            </tr>,
            <tr key={date.toISODate() + "5"}>
                <td colSpan={2} className="black bs-border-all" />
                {this.cpt_list.map(a => this.renderOperation(5, a, daySchedule, providerGroups, goals[a], this.dayGoalSelector, dayData[a], weekTotals[a]))}
            </tr>
        ];
    }

    private renderTotals(week: number, data: any, providerGroups: any[], goalData: any, month?: DateTime, daysWorked = 1) {
        const totalColumns = 2 + providerGroups.reduce((p, v) => p + v.length, 0) * this.cpt_list.length;
        goalData = goalData || {};
        const goalSelector = week === -1
            ? this.monthGoalSelector
            : week === -2
                ? this.yearGoalSelector
                : this.weekGoalSelector;

        return [
            <tr key={`total0${week}`}>
                {week === -1 ? undefined : <td colSpan={totalColumns} className="black bs-border-all">&nbsp;</td>}
            </tr>,
            <tr key={`totalw${week}`}>
                {
                    week === -1
                        ? <th colSpan={2} className="bs-week gray bs-border-all">{month ? formatDateLocalMonth(month) : "Month"} totals</th>
                        : week === -2
                            ? <th colSpan={2} className="bs-week gray bs-border-all">{month ? month.year : ""} totals</th>
                            : <th colSpan={2} className="bs-week gray bs-border-all">Week {week} totals</th>
                }
                {this.cpt_list.map(a => this.renderOperation(0, a, {}, providerGroups, goalData[a], goalSelector))}
            </tr>,
            <tr key={`total2${week}`} className="bs-names">
                <td colSpan={2} className="gray bs-border-lr" />
                {this.cpt_list.map(a => this.renderOperation(1, a, {}, providerGroups, goalData[a], goalSelector, data[a], undefined, true))}
            </tr>,
            <tr key={`total9${week}`} className="bs-row-multi">
                <th colSpan={2} className="bs-totals-heading bs-border-lr">Total per provider by location</th>
                {this.cpt_list.map(a => this.renderOperation(6, a, {}, providerGroups, goalData[a], goalSelector, data[a], undefined, true))}
            </tr>,
            <tr key={`total3${week}`}>
                <th colSpan={2} className="bs-totals-heading bs-border-lr">Total per provider</th>
                {this.cpt_list.map(a => this.renderOperation(2, a, {}, providerGroups, goalData[a], goalSelector, data[a], undefined, true))}
            </tr>,
            <tr key={`total4${week}`}>
                <th colSpan={2} className="bs-totals-heading bs-border-lr">Total per location</th>
                {this.cpt_list.map(a => this.renderOperation(3, a, {}, providerGroups, goalData[a], goalSelector, data[a], undefined, true))}
            </tr>,
            <tr key={`total5${week}`}>
                <th colSpan={2} className="bs-totals-heading bs-border-lr">Total per practice</th>
                {this.cpt_list.map(a => this.renderOperation(4, a, {}, providerGroups, goalData[a], goalSelector, data[a], undefined, true))}
            </tr>,
            <tr key={`total6${week}`}>
                <th colSpan={2} className="bs-totals-heading bs-border-lr bs-border-bottom">Total</th>
                {this.cpt_list.map(a => this.renderOperation(5, a, {}, providerGroups, goalData[a], goalSelector, data[a], undefined, true, daysWorked))}
            </tr>,
            <tr key={`total7${week}`}>
                <td colSpan={totalColumns} className="bs-spacer">&nbsp;</td>
            </tr>,
            <tr key={`total8${week}`}>
                <td colSpan={totalColumns} className="bs-spacer">&nbsp;</td>
            </tr>
        ];
    }

    private renderOperation(row: number, cpt: string, schedule: any, providerGroups: any[], goalData: any, goalSelector: (goal: any) => number, data?: any, weekTotals?: any, isTotals: boolean = false, colorMultiplier: number = 1) {
        const cptData = data || {};
        const isOff = !schedule;
        const goals = goalData || {};

        switch (row) {
            case 0:
                return <th key={cpt + row} colSpan={providerGroups.reduce((p, v) => p + v.length, 0)} className="col-center gray bs-border-all">{this.getSurgeryName(cpt)}</th>;
            case 1:
                return providerGroups.map(a => (
                    a.map((b: any, i: number, ar: any[]) => (
                        <th key={cpt + b.employeeId} className={`bs-${((schedule && schedule[b.employeeId]) || b.location).color} ${isOff ? "bs-off" : ""} ${!isTotals && schedule && !schedule[b.employeeId] ? "bs-off-sched" : ""} ${i === ar.length - 1 ? "bs-border-right" : ""}`} title={formatName(b.employee.name)}>{this.getNickname(b.employee.name)}</th>
                    ))
                ));
            case 2:
                return providerGroups.map(a => {
                    if (weekTotals) {
                        for (const prov of a) {
                            if (!weekTotals[prov.employeeId]) {
                                weekTotals[prov.employeeId] = { count: 0 };
                            }
                            if (!weekTotals[prov.employeeId].locations) {
                                weekTotals[prov.employeeId].locations = {};
                            }
                            const location = ((schedule && schedule[prov.employeeId]) || prov.location);
                            const count: number = ((cptData && cptData[prov.employeeId]) || {}).count || 0;

                            weekTotals[prov.employeeId].count += count

                            // Location-specific data
                            if (!weekTotals[prov.employeeId].locations[location.id]) {
                                weekTotals[prov.employeeId].locations[location.id] = { count: 0, color: location.color };
                            }
                            weekTotals[prov.employeeId].locations[location.id].count += count;
                        }
                    }

                    return a.map((b: any, i: number, ar: any[]) => {
                        let count = ((cptData && cptData[b.employeeId]) || {}).count || 0;
                        let hasReachedGoal = goals.employees && goals.employees[b.employeeId] && goalSelector(goals.employees[b.employeeId]) && count >= goalSelector(goals.employees[b.employeeId]);
                        return (
                            <th key={cpt + b.employeeId} className={`bs-${((schedule && schedule[b.employeeId]) || b.location).color} ${isOff && "bs-off"} ${!isTotals && schedule && !schedule[b.employeeId] ? "bs-off-sched" : ""} ${i === ar.length - 1 ? "bs-border-right" : ""}${hasReachedGoal ? " bs-goal" : ""}`}>
                                {count || "-" /* \u00A0 = nbsp */}
                            </th>
                        );
                    });
                });
            case 3:
                return providerGroups.map((a: any[]) => {
                    const locations: { [id: string]: any } = {};

                    // Gather data about number of providers in each home location
                    for (const prov of a) {
                        if (!locations[prov.homeLocationId]) {
                            locations[prov.homeLocationId] = { color: prov.location.color, count: 0, total: 0, home: true };
                        }

                        locations[prov.homeLocationId].count++;
                    }

                    // Compute totals by location
                    for (const prov of a) {
                        const totalData = (cptData && cptData[prov.employeeId]) || {};
                        if (totalData.locations) {
                            for (const id of Object.keys(totalData.locations)) {
                                locations[id].total += totalData.locations[id].count || 0;
                            }
                        }
                        else {
                            const total = totalData.count || 0;
                            const provLocation = (schedule && schedule[prov.employeeId] && schedule[prov.employeeId].id) || prov.homeLocationId;

                            if (!locations[provLocation]) {
                                const locationColor = (schedule && schedule[prov.employeeId] && schedule[prov.employeeId].color) || prov.location.color;
                                locations[provLocation] = { color: locationColor, count: 0, total: 0, home: false };
                            }

                            locations[provLocation].total += total;
                            if (!locations[provLocation].home) {
                                locations[provLocation].count++;
                            }
                        }
                    }

                    return Object.keys(locations).map((b, i, a) => {
                        let hasReachedGoal = goals.locations && goals.locations[b] && goalSelector(goals.locations[b]) && locations[b].total >= goalSelector(goals.locations[b]);

                        return (
                            <td key={cpt + b} colSpan={locations[b].count} className={`col-bold bs-${locations[b].color} ${isOff ? "bs-off" : ""} ${(i === a.length - 1 ? "bs-border-right" : "")}${hasReachedGoal ? " bs-goal" : ""}`}>{locations[b].total}</td>
                        );
                    });
                });
            case 4:
                return providerGroups.map((a: any[], i: number) => {
                    let total = 0;
                    for (const prov of a) {
                        total += ((cptData && cptData[prov.employeeId]) || {}).count || 0;
                    }

                    let hasReachedGoal = goals.groups && goals.groups[i] && goalSelector(goals.groups[i]) && total >= goalSelector(goals.groups[i]);

                    return <td key={`${cpt}${i}total`} colSpan={a.length} className={`col-bold bs-${i === 0 ? "brick" : "deep-pink"} ${isOff ? "bs-off" : ""} bs-border-br${hasReachedGoal ? " bs-goal" : ""}`}>{total}</td>
                });
            case 5:
                let total = 0;
                let totalLength = 0;
                for (const group of providerGroups) {
                    for (const prov of group) {
                        total += ((cptData && cptData[prov.employeeId]) || {}).count || 0;
                    }
                    totalLength += group.length;
                }

                let hasReachedGoal = this.state.showGoals && goalSelector(goals) && total >= goalSelector(goals);

                const color = this.state.showGoals && (cpt === TOTALS_CPT && !isOff && colorMultiplier) ? this.calculateColor(total / colorMultiplier) : "default";

                return <td key={cpt + row} colSpan={totalLength} className={`col-bold gray bs-border-br${hasReachedGoal ? " bs-goal" : ""} bs-range-${color}`}>
                    {total}
                </td>;
            case 6:
                return providerGroups.map(a => {
                    return a.map((b: any, i: number, ar: any[]) => {
                        const totalData = (cptData && cptData[b.employeeId]) || {};
                        const locations = totalData.locations ? Object.keys(totalData.locations) : [];

                        if (locations.length > 0) {
                            return <th key={cpt + b.employeeId} className={`bs-${b.location.color || ""} ${i === ar.length - 1 ? "bs-border-right" : ""}`}>
                                <div className="bs-values-multi">
                                    {
                                        locations.map(c => (
                                            <span key={c} className={`bs-${totalData.locations[c].color}`}>{totalData.locations[c].count || "-"}</span>
                                        ))
                                    }
                                </div>
                            </th>;
                        }
                        else {
                            return <th key={cpt + b.employeeId} className={`bs-${b.location.color || ""} ${i === ar.length - 1 ? "bs-border-right" : ""}`}>
                                {(totalData.count || "-") /* \u00A0 = nbsp */}
                            </th>;
                        }
                    });
                });
            default:
                return <td />
        }
    }

    private async loadData(background = false) {
        try {
            this.setState({
                isLoading: !background
            });
            const after = DateTime.fromISO(this.state.month);
            const before = after.plus({ month: 1 });

            const parameters = [
                `after=${after.toISODate()}`,
                `before=${before.toISODate()}`
            ];
            if (this.state.statusFilter) {
                parameters.push(`status=${this.state.statusFilter}`);
            }

            const ytdParameters = [
                `after=${after.startOf("year")}`,
                `before=${before.toISODate()}`,
            ];
            if (this.state.statusFilter) {
                ytdParameters.push(`status=${this.state.statusFilter}`);
            }

            const scheduleParameters = [
                `after=${after.startOf("year")}`,
                `before=${before.toISODate()}`,
            ];

            const [caseStats, ytdCaseStats, scheduleData, goals, colors] = await Promise.all([
                V2.get<any>(`metrics/case-stats?${parameters.join("&")}`),
                V2.get<any>(`metrics/case-stats?${ytdParameters.join("&")}`),
                V2.get<any[]>(`schedule?group=${WellKnownGroups.ProviderStats}&${scheduleParameters.join("&")}`),
                V2.get<any>(`metrics/case-stats/goals?${parameters.join("&")}`),
                V2.get<any>(`metrics/case-stats/colors`),
            ]);

            if (!caseStats.providers || Object.keys(caseStats.providers).length === 0) {
                throw new Error("No provider information available");
            }

            // Calculate day totals
            for (const key of Object.keys(caseStats.data)) {
                const dayData = caseStats.data[key];

                const dayTotals: { [prov: string]: any } = {};
                this.cpt_list.forEach(cpt => {
                    const data = dayData[cpt] || {};
                    Object.keys(data).forEach(np => {
                        if (!dayTotals[np]) {
                            dayTotals[np] = { count: 0 };
                        }

                        dayTotals[np].count += data[np].count;
                    });
                });

                dayData[TOTALS_CPT] = dayTotals;
            }

            const schedule: { [date: string]: { [id: string]: any } } = {};
            const ytdSchedule: { [date: string]: { [id: string]: any } } = {};
            for (const item of scheduleData) {
                for (let i = 0; i < item.length; i++) {
                    const newDate = DateTime.fromISO(item.date).plus({ day: i });
                    if (!newDate.hasSame(after, "year")) {
                        continue;
                    }

                    if (newDate.hasSame(after, "month")) {
                        if (!schedule[newDate.toISODate()]) {
                            schedule[newDate.toISODate()] = {};
                        }
                        if (!schedule[newDate.toISODate()][item.employee.id]) {
                            schedule[newDate.toISODate()][item.employee.id] = item.location;
                        }
                    }

                    if (!ytdSchedule[newDate.toISODate()]) {
                        ytdSchedule[newDate.toISODate()] = {};
                    }
                    if (!ytdSchedule[newDate.toISODate()][item.employee.id]) {
                        ytdSchedule[newDate.toISODate()][item.employee.id] = item.location;
                    }
                }
            }

            // Find all scheduled locations
            const locations: { [id: string]: any } = {};
            const ytdLocations: { [id: string]: any } = {};
            for (const item of scheduleData) {
                if (!ytdLocations[item.location.id]) {
                    ytdLocations[item.location.id] = { id: item.location.id, name: item.location.name, color: item.location.color };
                }

                // Consider a schedule entry as being within the month if it's start or end dates are inside the current month
                const segmentStart = DateTime.fromISO(item.date);
                const segmentEnd = segmentStart.plus({ days: item.length });
                if ((segmentStart.hasSame(after, "month") || segmentEnd.hasSame(after, "month")) && !locations[item.location.id]) {
                    locations[item.location.id] = { id: item.location.id, name: item.location.name, color: item.location.color };
                }
            }

            // Take spans and create entries for each week
            let testDate = after.startOf("year").startOf("week").minus({ day: 1 });
            let groups = caseStats.providers[Object.keys(caseStats.providers)[0]];
            let ytdGroups = ytdCaseStats.providers[Object.keys(ytdCaseStats.providers)[0]];
            const providerGroups: { [date: string]: { groups: any[] } } = {};
            const ytdProviderGroups: { [date: string]: { groups: any[] } } = {};
            while (testDate < before) {
                ytdProviderGroups[testDate.toISODate()] = ytdGroups;

                if (testDate.diff(after.startOf("week").minus({ day: 1 }), "days").days >= 0) {
                    providerGroups[testDate.toISODate()] = groups;
                }

                testDate = testDate.plus({ day: 7 });
                if (caseStats.providers[testDate.toISODate()]) {
                    groups = caseStats.providers[testDate.toISODate()];
                }
                if (ytdCaseStats.providers[testDate.toISODate()]) {
                    ytdGroups = ytdCaseStats.providers[testDate.toISODate()];
                }
            }

            // Fill in goals for all days based on previous goals
            testDate = after;
            let goal = goals[Object.keys(goals)[0]];
            while (testDate < before) {
                const dateString = testDate.toISODate();
                if (!goals[dateString]) {
                    goals[dateString] = goal;
                }
                else {
                    goal = goals[dateString];
                }

                testDate = testDate.plus({ day: 1 });
            }

            // Add placeholder entries for any location that was scheduled, but no one has that place as a home location
            for (const date of Object.keys(providerGroups)) {
                const missing = Object.keys(locations).filter(a => !providerGroups[date].groups.flat(1).some(b => a === b.homeLocationId));
                for (const key of missing) {
                    const group = {
                        employeeId: "P" + key,
                        employee: {
                            id: "P" + key,
                            name: { first: "-", last: "" },
                            locationId: key
                        },
                        group: 0,
                        homeLocationId: key,
                        location: locations[key]
                    };
                    providerGroups[date].groups[0].push(group);
                    providerGroups[date].groups[0].sort((a: any, b: any) => a.homeLocationId.localeCompare(b.homeLocationId));

                    if (ytdProviderGroups[date]?.groups[0].some((a: any) => a.location.id !== key)) {
                        ytdProviderGroups[date].groups[0].push(group);
                    }
                }
            }

            // Add placeholders for any location in the YTD data that no one this month was scheduled for, nor was it a home location for anyone
            for (const date of Object.keys(ytdProviderGroups)) {
                const missing = Object.keys(ytdLocations).filter(a => !ytdProviderGroups[date].groups.flat(1).some(b => a === b.homeLocationId));
                for (const key of missing) {
                    const group = {
                        employeeId: "P" + key,
                        employee: {
                            id: "P" + key,
                            name: { first: "-", last: "" },
                            locationId: key
                        },
                        group: 0,
                        homeLocationId: key,
                        location: ytdLocations[key]
                    };
                    ytdProviderGroups[date].groups[0].push(group);
                    ytdProviderGroups[date].groups[0].sort((a: any, b: any) => a.homeLocationId.localeCompare(b.homeLocationId));
                }
            }

            // Get union of all providers that were on any week during the month
            const totalProviders: { [id: string]: any }[] = [{}, {}];
            const ytdTotalProviders: { [id: string]: any }[] = [{}, {}];
            for (const date of Object.keys(providerGroups)) {
                for (let i = 0; i < providerGroups[date].groups.length; i++) {
                    for (const prov of providerGroups[date].groups[i]) {
                        totalProviders[i][prov.employeeId] = prov;
                    }
                }
            }
            for (const date of Object.keys(ytdProviderGroups)) {
                for (let i = 0; i < ytdProviderGroups[date].groups.length; i++) {
                    for (const prov of ytdProviderGroups[date].groups[i]) {
                        ytdTotalProviders[i][prov.employeeId] = prov;
                    }
                }
            }

            const totalProviderGroups = totalProviders.map(a => Object.keys(a).map(b => a[b]).sort((f, s) => f.homeLocationId.localeCompare(s.homeLocationId) || f.employee.name.first.localeCompare(s.employee.name.first)));
            const ytdTotalProviderGroups = ytdTotalProviders.map(a => Object.keys(a).map(b => a[b]).sort((f, s) => f.homeLocationId.localeCompare(s.homeLocationId) || f.employee.name.first.localeCompare(s.employee.name.first)));

            const ytdTotalData = this.calculateYtdTotals(
                after.startOf("year"),
                before,
                ytdCaseStats.data,
                ytdSchedule,
                ytdTotalProviderGroups
            );

            this.setState({
                isLoading: false,

                providerGroups,
                providerGroupsMonth: { groups: totalProviderGroups },
                data: caseStats.data,

                ytdProviderGroupsMonth: { groups: ytdTotalProviderGroups },
                ytdData: ytdTotalData,

                schedule,

                goals,

                colors
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e.toString() || "Could not load data"
            });
        }
    }

    private calculateYtdTotals(start: DateTime, end: DateTime, data: { [date: string]: any }, scheduleData: { [date: string]: { [id: string]: any } }, providerGroups: any[][]) {
        const totalData: { [cpt: string]: any } = {};
        this.cpt_list.forEach(a => totalData[a] = {});

        for (const date of Object.keys(data)) {
            const dayData = data[date];
            const schedule = scheduleData[date];

            // Gather fake totals "surgery" info
            const dayTotals: { [prov: string]: any } = {};
            this.cpt_list.forEach(a => {
                const data = dayData[a] || {};
                Object.keys(data).forEach(b => {
                    if (!dayTotals[b]) {
                        dayTotals[b] = { count: 0 };
                    }

                    dayTotals[b].count += data[b].count;
                });
            });

            dayData[TOTALS_CPT] = dayTotals;

            for (const cpt of this.cpt_list) {
                const cptData = dayData[cpt] || {};
                const cptTotal = totalData[cpt];

                for (const group of providerGroups) {
                    for (const prov of group) {
                        if (!cptTotal[prov.employeeId]) {
                            cptTotal[prov.employeeId] = { count: 0 };
                        }
                        if (!cptTotal[prov.employeeId].locations) {
                            cptTotal[prov.employeeId].locations = {};
                        }
                        const location = ((schedule && schedule[prov.employeeId]) || prov.location);
                        const count: number = ((cptData && cptData[prov.employeeId]) || {}).count || 0;

                        cptTotal[prov.employeeId].count += count

                        // Location-specific data
                        if (!cptTotal[prov.employeeId].locations[location.id]) {
                            cptTotal[prov.employeeId].locations[location.id] = { count: 0, color: location.color };
                        }
                        cptTotal[prov.employeeId].locations[location.id].count += count;
                    }
                }
            }
        }

        const yearTotal: { [cpt: string]: any } = {};
        this.cpt_list.forEach(cpt => {
            yearTotal[cpt] = {}
            Object.keys(totalData[cpt]).forEach(np => {
                if (!yearTotal[cpt][np]) {
                    yearTotal[cpt][np] = {
                        count: 0, locations: {}
                    };
                }

                yearTotal[cpt][np].count += totalData[cpt][np].count || 0;
                for (const key of Object.keys(totalData[cpt][np].locations)) {
                    if (!yearTotal[cpt][np].locations[key]) {
                        yearTotal[cpt][np].locations[key] = { count: 0, color: totalData[cpt][np].locations[key].color };
                    }

                    yearTotal[cpt][np].locations[key].count += totalData[cpt][np].locations[key].count;
                }
            });
        });

        return totalData;
    }

    private getNickname(name: { first: string, last: string }) {
        const first: any[] = name.first.match(/[A-Z-]/g) || [];
        const last: any[] = name.last.match(/[A-Z]/g) || [];
        return first.concat(last).join("");
    }

    private getSurgeryName(cpt: string) {
        return ({
            "58558": "D&C",
            "58563": "Ablation",
            "57288": "TVT",
            "58570": "Hysterectomy",
            "58660": "Op lap",
            [TOTALS_CPT]: "Totals"
        } as { [cpt: string]: string })[cpt];
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            isLoading: true,
            [name]: value
        }, () => this.loadData());
    }

    private dayGoalSelector(goal: any) {
        return goal.dayTarget;
    }

    private weekGoalSelector(goal: any) {
        return goal.weekTarget;
    }

    private monthGoalSelector(goal: any) {
        return goal.monthTarget;
    }

    private yearGoalSelector(goal: any) {
        return undefined; // TODO: Add ytd targets
    }

    private calculateColor(value: number) {
        for (const color of this.state.colors) {
            if (value >= color.value) {
                return color.color;
            }
        }

        return "";
    }

    private handleTabChange(tabIndex: number) {
        let statusFilter = "";
        let showGoals = true;
        switch (tabIndex) {
            default:
            case 0: statusFilter = ""; break;
            case 1: statusFilter = "complete"; showGoals = false; break;
            case 2: statusFilter = "canceled,noshow"; showGoals = false; break;
            case 3: statusFilter = "pending,active"; showGoals = false; break;
        }

        this.setState({
            isLoading: true,

            statusFilter,
            showGoals
        }, () => this.loadData());
    }

    private toggleRefresh() {
        const isRefreshEnabled = this.state.isRefreshEnabled;
        this.setState({
            isRefreshEnabled: !isRefreshEnabled
        });
    }
}

export default withTitle("Case Stats", MetricsBigSheetView);